/**
 * Shared helpers for candidate document URLs and preview support.
 *
 * Documents are served by the backend from /api/files/** with
 * `Content-Disposition: inline` and a real content type, and that path is permitAll
 * in SecurityConfig — so previewable types render directly in an <iframe> with no
 * token handshake.
 */

export interface ViewerDocument {
  /** Stable identifier, unique within a document list. */
  id: string;
  /** Tab label, e.g. "Resume". */
  label: string;
  /** Raw URL as stored on the application; resolved against the API base when displayed. */
  url: string;
}

export interface CandidateDocumentSource {
  resumeUrl?: string | null;
  coverLetterUrl?: string | null;
  portfolioUrl?: string | null;
}

/** True for an absolute http(s) URL, i.e. something we did not upload. */
export const isExternalLink = (url: string): boolean => /^https?:\/\//i.test(url || '');

/**
 * Path to request through `axiosInstance`, whose baseURL is already '/api'.
 *
 * Documents are fetched as blobs over XHR rather than pointed at directly from an
 * <iframe>/<a>: CRA's dev proxy refuses to forward GETs that accept text/html (it serves
 * index.html instead, so a framed document renders the app's landing page). An XHR is
 * proxied normally, behaves the same under nginx in prod, and carries the auth header if
 * /api/files/** ever stops being public.
 *
 * Stored URLs may or may not already carry the /api prefix; strip it so it isn't doubled.
 */
export const toApiPath = (url: string): string => {
  if (!url) return '';
  const path = url.startsWith('/') ? url : `/${url}`;
  return path.startsWith('/api/') ? path.slice(4) : path;
};

/** File name (decoded, without query/hash) for display and downloads. */
export const getFileName = (url: string): string => {
  if (!url) return '';
  const clean = url.split('?')[0].split('#')[0];
  const name = clean.substring(clean.lastIndexOf('/') + 1);
  try {
    return decodeURIComponent(name);
  } catch {
    return name;
  }
};

export const getFileExtension = (url: string): string => {
  const name = getFileName(url);
  const dot = name.lastIndexOf('.');
  return dot === -1 ? '' : name.slice(dot + 1).toLowerCase();
};

export type DocumentPreviewKind = 'pdf' | 'image' | 'text' | 'docx' | 'unsupported';

const PREVIEW_KINDS: Record<string, DocumentPreviewKind> = {
  pdf: 'pdf',
  docx: 'docx',
  txt: 'text',
  png: 'image',
  jpg: 'image',
  jpeg: 'image',
  gif: 'image',
  webp: 'image',
};

/**
 * Browsers render PDFs, plain text and images natively; .docx is rendered in the browser
 * by docx-preview, so candidate documents are never sent to a third-party viewer.
 *
 * Legacy .doc stays unsupported on purpose — it is the old OLE binary format, which
 * docx-preview cannot read — so those fall back to download / open-in-new-tab.
 */
export const getPreviewKind = (url: string): DocumentPreviewKind =>
  PREVIEW_KINDS[getFileExtension(url)] || 'unsupported';

/**
 * The documents shown in the viewer for one application.
 *
 * A portfolio is usually a link to an external site rather than an uploaded file, and
 * external sites generally refuse to be framed (X-Frame-Options), so only uploaded
 * portfolios are previewable — external ones stay plain links at the call site.
 */
export const buildCandidateDocuments = (application: CandidateDocumentSource): ViewerDocument[] => {
  const documents: ViewerDocument[] = [];
  if (application.resumeUrl) {
    documents.push({ id: 'resume', label: 'Resume', url: application.resumeUrl });
  }
  if (application.coverLetterUrl) {
    documents.push({ id: 'coverLetter', label: 'Cover Letter', url: application.coverLetterUrl });
  }
  if (application.portfolioUrl && !isExternalLink(application.portfolioUrl)) {
    documents.push({ id: 'portfolio', label: 'Portfolio', url: application.portfolioUrl });
  }
  return documents;
};
